// Analytics: Evidence feature (chat, suggestions, sources, sign-up walls)

import type { SubscriptionTier } from '@heidi/schema/ml-scribe';

import type { HeidiButtonDestination } from '@/components/chat/config/heidi-button-allowlist';

import type { CapabilitySignallingVariant } from '@/features/flags/capability-signalling-variant';

import type { ChipOrigin, ChipSurface, MessageEntryMethod } from './chat';

/**
 * Feature identifier for Evidence-related events.
 * Used in generic events (button_clicked, feedback_submitted) to indicate Evidence context.
 */
export const EVIDENCE_FEATURE = 'evidence' as const;

/**
 * Evidence location context - where the user is when triggering the event.
 * - logged_out: /chat route (no auth, anonymous)
 * - in_session: /scribe/session route (within an active scribe session)
 * - out_of_session: /scribe/chat route (logged in but not in a session)
 */
export enum EvidenceLocation {
  LoggedOut = 'logged_out',
  InSession = 'in_session',
  InSessionCentral = 'in_session_central',
  OutOfSession = 'out_of_session',
}

/**
 * Evidence source selection mode.
 */
export type EvidenceSourceSelectionMode = 'default' | 'preset' | 'custom';

/**
 * Base properties common to most Evidence events.
 */
export interface EvidenceBaseEventProps {
  /** Where the user is: logged_out, in_session, or out_of_session */
  evidence_location?: EvidenceLocation;
  /** User's region (e.g., "Australia", "United States") */
  region?: string;
  /** Whether user has Evidence Pro subscription */
  is_evidence_pro?: boolean;
  /** Whether user is on team */
  is_team_member?: boolean;
  /** Whether the active workspace is managed (enterprise/org-managed); undefined until a workspace is active */
  is_managed?: boolean;
}

/**
 * Properties for sidebar click event.
 */
export type EvidenceSidebarClickedEventProps = EvidenceBaseEventProps;

/**
 * Properties for sources list click events.
 */
export type EvidenceSourcesListClickedEventProps = EvidenceBaseEventProps;

export interface EvidenceSourcesListPresetClickedEventProps extends EvidenceBaseEventProps {
  /** Which preset was clicked */
  preset_type: 'primary_evidence' | 'local_guidelines' | 'web_search';
  /** Whether it was enabled or disabled */
  is_enabled: boolean;
}

export interface EvidenceSourcesListCustomClickedEventProps extends EvidenceBaseEventProps {
  /** Number of custom sources selected */
  custom_sources_count: number;
}

export interface EvidenceSourcesListLocalGuidelinesClickedEventProps extends EvidenceBaseEventProps {
  /** Whether it was enabled or disabled */
  is_enabled: boolean;
}

export interface EvidenceSourcesListWebSearchClickedEventProps extends EvidenceBaseEventProps {
  /** Whether it was enabled or disabled */
  is_enabled: boolean;
}

/**
 * Properties for the "Check other sources" dropdown open event.
 * Fired when the user opens the dropdown on an Evidence chat response to
 * re-run the same query against a different set of sources.
 */
export interface EvidenceCheckOtherSourcesClickedEventProps extends EvidenceBaseEventProps {
  /** Chat ID */
  chat_id?: string;
  /** Backend message ID (msg_*) of the response whose control was clicked */
  message_id?: string;
  /** Session ID if in session */
  session_id?: string;
}

/**
 * Properties for chat submission event.
 */
export interface EvidenceChatSubmittedEventProps extends EvidenceBaseEventProps {
  /** Length of the query in characters */
  query_length: number;
  /** Number of sources selected */
  sources_count?: number;
  /** List of source domains selected */
  sources?: string[];
  /** Selection mode: default, preset, or custom */
  selection_mode: EvidenceSourceSelectionMode;
  /** Preset settings if using preset mode */
  preset_primary_evidence?: boolean;
  preset_local_guidelines?: boolean;
  preset_web_search?: boolean;
  /** Chat ID */
  chat_id?: string;
  /** Backend message ID (msg_*) */
  message_id?: string;
  /** Session ID if in session */
  session_id?: string;
  /** Session stage for in-session submissions */
  session_stage?: 'pre' | 'during' | 'post';
  /** How the message was initiated */
  entry_method?: MessageEntryMethod;
  /** Length in words */
  word_count?: number;
  /** Chip display label (shortcut/suggestion only) */
  chip_label?: string;
  /** Chip category (shortcut/suggestion only) */
  chip_category?: string;
  /** Pin origin: Heidi default, marketplace library, or user custom (shortcut chips) */
  origin?: ChipOrigin;
  /** UI surface that produced the click (chip / banner clicks) */
  surface?: ChipSurface;
  /** True when dispatched from the message queue (auto-send / "Send now") */
  queued?: boolean;
  /** Wait time in the queue before dispatch, ms. Omitted for immediate sends. */
  queue_duration_ms?: number;
}

/**
 * Properties for chat completion event.
 */
export interface EvidenceChatCompletedEventProps extends EvidenceBaseEventProps {
  /** Total time from submit to completion in milliseconds */
  duration_ms: number;
  /** User's current billing plan at the time of the event. */
  current_plan?: SubscriptionTier;
  /** Time to first token in milliseconds */
  time_to_first_token_ms?: number;
  /** Number of references/sources in the response */
  references_count: number;
  /** Query length in characters */
  query_length: number;
  /** Response length in characters */
  response_length?: number;
  /** Chat ID */
  chat_id?: string;
  /** Backend message ID (msg_*) */
  message_id?: string;
  /** Session ID if in session */
  session_id?: string;
  /** Number of Tavily web searches performed */
  tavily_search_count?: number;
  /** EVI-731: The mode the backend routed to (from mode_info chunk) */
  routed_mode?: 'legacy' | 'evidence';
  /** Tools used in this response (e.g., 'evidence_search', 'medical_calculator') */
  tools_used?: string[];
  /** Length in words */
  word_count?: number;
  /** How the original message was initiated */
  entry_method?: MessageEntryMethod;
  /** Chip display label (shortcut/suggestion only) */
  chip_label?: string;
  /** Chip category (shortcut/suggestion only) */
  chip_category?: string;
  /** Pin origin: Heidi default, marketplace library, or user custom (shortcut chips) */
  origin?: ChipOrigin;
  /** UI surface that produced the click (chip / banner clicks) */
  surface?: ChipSurface;
  /** True when the original message was dispatched from the queue */
  queued?: boolean;
  /** Wait time in the queue before dispatch, ms. Omitted for immediate sends. */
  queue_duration_ms?: number;
  /**
   * True when the completed response included a quota-exhausted paywall (dedicated
   * chunk or streamed text with a markdown link to `/settings/billing`).
   * Omitted when no paywall message was shown.
   */
  quota_exhausted_message_shown?: boolean;
}

/**
 * Discriminates the source of an error event.
 *
 * Cross-repo PHI contract: this union — together with the message-send `result`
 * union and `routed_mode` — is promoted to a durable Datadog RUM custom metric in
 * ml-scribe (`monitoring/modules/evidence/rum_message_send_outcome.tf`, ~15-month
 * retention, no server-side cardinality/PHI cap). Keep these values bounded,
 * PHI-free enums: widening any of them to free-form or clinical text would
 * silently persist PHI as a metric tag for ~15 months. Any change here warrants a
 * review of that backend metric contract.
 */
export type EvidenceChatErrorType =
  | 'stream_mid'
  | 'stream_start'
  // EVI-2117: a backend that advertised terminal-sentinel support closed the SSE
  // stream cleanly without the sentinel — the turn was truncated mid-answer
  // (ALB idle-timeout / worker SIGTERM / proxy close) rather than completed.
  | 'clean_close_truncated'
  // The SSE promise settled without a terminal reaching the consumer.
  // `silent_stream_stall` — resolved on its own (no onclose/onerror), the
  // pathological case behind the invisible 5-min timeouts; `client_abort` — an
  // abort signal settled it (user cancel / new send), benign and filterable.
  | 'silent_stream_stall'
  | 'client_abort'
  // The SSE idle-timeout fired — no SSE activity (incl.
  // heartbeats) for STREAM_IDLE_TIMEOUT_MS. Split out from `stream_mid` so the
  // "response took too long" cohort is filterable on its own, since the backend
  // usually recorded `outcome:success` and left no retained trace.
  | 'idle_no_activity'
  | 'create_chat'
  // Historical. EVI-2557 stopped emitting this: sends are no longer blocked on
  // navigator.onLine. Real transport failures use create_chat / stream_*.
  | 'offline_pre_submit'
  | 'load_chat'
  | 'block'
  | 'attachment'
  | 'document_delete'
  | 'feedback'
  | 'unauthenticated_tool'
  | 'create_collection';

export type EvidenceChatBlockType =
  | 'template_search'
  | 'sessions_by_date'
  | 'patient_search'
  | 'create_template'
  | 'edit_template_section'
  | 'update_word_library'
  | 'open_template_editor'
  | 'pin_template'
  | 'unpin_template';

export type EvidenceChatAttachmentReason =
  | 'file_too_large'
  | 'unsupported_type'
  | 'rate_limited'
  | 'upload_failed';

/**
 * Properties for chat error event.
 */
export interface EvidenceChatErrorEventProps extends EvidenceBaseEventProps {
  /** Error message */
  error_message?: string;
  /** Error code if available */
  error_code?: string;
  /** Time until error occurred in milliseconds */
  duration_ms?: number;
  /** Query length in characters (optional — not all error paths have a query) */
  query_length?: number;
  /** Chat ID */
  chat_id?: string;
  /** Session ID if in session */
  session_id?: string;
  /** EVI-731: The mode the backend routed to (from mode_info chunk) */
  routed_mode?: 'legacy' | 'evidence';
  /** Discriminates which error path fired this event */
  error_type?: EvidenceChatErrorType;
  /** HTTP status code when available */
  http_status?: number;
  /** Block type for block-level errors */
  block_type?: EvidenceChatBlockType;
  /** Reason for attachment errors */
  attachment_reason?: EvidenceChatAttachmentReason;
}

/**
 * Properties for chat abort event.
 */
export interface EvidenceChatAbortedEventProps extends EvidenceBaseEventProps {
  /** Time until user aborted in milliseconds */
  duration_ms?: number;
  /** Query length in characters */
  query_length: number;
  /** Chat ID */
  chat_id?: string;
  /** Backend message ID (msg_*) of the in-flight assistant response being aborted */
  message_id?: string;
  /** Session ID if in session */
  session_id?: string;
}

/**
 * Properties for pro trial button start event.
 */
export type EvidenceProTrialButtonStartedEventProps = EvidenceBaseEventProps;

/**
 * Properties for trial started event (when checkout is successful).
 */
export interface EvidenceTrialStartedEventProps extends EvidenceBaseEventProps {
  /** Subscription period selected */
  period: 'monthly' | 'yearly';
  /** Subscription tier */
  tier: string;
}

/**
 * Properties for deep link sign in/up events.
 */
export interface EvidenceDeepLinkSignInClickedEventProps {
  /** Location where clicked */
  evidence_location: EvidenceLocation;
}

export interface EvidenceDeepLinkSignUpClickedEventProps {
  /** Location where clicked */
  evidence_location: EvidenceLocation;
}

/**
 * Properties for new chat click event.
 */
export interface EvidenceNewChatClickedEventProps extends EvidenceBaseEventProps {
  /** Where the click originated from */
  click_source: 'sidebar' | 'header' | 'history_overlay';
}

/**
 * Properties for delete chat click event.
 */
export interface EvidenceDeleteChatClickedEventProps extends EvidenceBaseEventProps {
  /** Chat ID being deleted */
  chat_id: string;
}

/**
 * Properties for save chat click event.
 */
export interface EvidenceSaveChatClickedEventProps extends EvidenceBaseEventProps {
  /** Chat ID being saved */
  chat_id: string;
  /** Where the save action was triggered from */
  click_source: 'sidebar' | 'header' | 'saved_section';
}

/**
 * Properties for unsave chat click event.
 */
export interface EvidenceUnsaveChatClickedEventProps extends EvidenceBaseEventProps {
  /** Chat ID being unsaved */
  chat_id: string;
  /** Where the unsave action was triggered from */
  click_source: 'sidebar' | 'header' | 'saved_section';
}

/**
 * Properties for past session click event.
 */
export interface EvidenceClickPastSessionClickedEventProps extends EvidenceBaseEventProps {
  /** Chat ID being selected */
  chat_id: string;
}

/**
 * Properties for sidebar open/close events.
 */
export type EvidenceChatSidebarOpenEventProps = EvidenceBaseEventProps;

export type EvidenceChatSidebarCloseEventProps = EvidenceBaseEventProps;

/**
 * Properties for toast shown event.
 */
export interface EvidenceToastShownEventProps extends EvidenceBaseEventProps {
  /** Chat ID that completed */
  chat_id: string;
}

/**
 * Properties for in-session advanced evidence credit consumption.
 */
export interface CreditsConsumedEventProps {
  /** Advanced evidence credits remaining after this consumption. */
  credits_remaining: number;
  /** User's billing tier at consumption time. */
  current_plan?: SubscriptionTier;
  /** Upgrade destination tier (Clinician or Practice). */
  target_plan?: SubscriptionTier;
}

/**
 * Properties for toast view clicked event.
 */
export interface EvidenceToastViewClickedEventProps extends EvidenceBaseEventProps {
  /** Chat ID being viewed */
  chat_id: string;
}

/**
 * Properties for language changed event from evidence.
 */
export interface EvidenceLanguageChangedEventProps extends EvidenceBaseEventProps {
  /** The new language code selected */
  language_code: string;
}

/**
 * Properties for specialty changed event from evidence.
 */
export interface EvidenceSpecialtyChangedEventProps extends EvidenceBaseEventProps {
  /** The new specialty ID selected */
  specialty_id: string;
}

/**
 * Properties for jurisdiction changed event from evidence.
 */
export interface EvidenceJurisdictionChangedEventProps extends EvidenceBaseEventProps {
  /** The new jurisdiction code selected (e.g., "AU-NSW", "US-CA") */
  jurisdiction_code: string;
}

/**
 * Evidence category options shown on the home screen.
 * These are the filter chips like "Quick clinical answers", "Diagnostic workups", etc.
 */
export enum EvidenceCategory {
  QuickClinicalAnswers = 'quick_clinical_answers',
  DiagnosticWorkups = 'diagnostic_workups',
  MedicationSafetyAndDosing = 'medication_safety_and_dosing',
  TreatmentDecisions = 'treatment_decisions',
  CompareTheEvidence = 'compare_the_evidence',
  LearningAndExamPrep = 'learning_and_exam_prep',
  TopicOverviews = 'topic_overviews',
}

/**
 * Sign-up wall types that can trigger a sign-up prompt.
 */
export enum EvidenceSignUpWall {
  HistoryWall = 'history_wall',
  SourceSelectionWall = 'source_selection_wall',
  QueryLimitWall = 'query_limit_wall',
  TranscribeWall = 'transcribe_wall',
  ActionsWall = 'actions_wall',
  BannerWall = 'banner_wall',
  ToolAccessWall = 'tool_access_wall',
  ExternalTriggerWall = 'external_trigger_wall',
}

/**
 * Properties for tracking when a user clicks on a category chip/option.
 * Event: evidence_category_clicked
 */
export type EvidenceCategoryClickedEventProps = {
  /** The category that was clicked */
  category: EvidenceCategory;
  /** Whether the category was selected (true) or deselected (false) */
  is_selected: boolean;
};

/**
 * Properties for tracking when a user clicks on a suggestion card.
 * Event: evidence_suggestion_clicked
 */
export type EvidenceSuggestionClickedEventProps = {
  /** The category (legacy) or verb of the suggestion */
  category: EvidenceCategory | EvidenceVerb;
  /** The question text that was clicked */
  question: string;
  /** Which surface the suggestion was clicked on. See the verb event's note. */
  evidence_location?: EvidenceLocation;
  /**
   * Assigned in-session capability-signalling arm. Amplitude-only — the
   * published Snowplow schema (1-0-1) does not include this field, so the
   * evidence Snowplow handler strips it before Iglu.
   */
  experiment_arm?: CapabilitySignallingVariant;
};

/**
 * Properties for tracking when a sign-up CTA is triggered.
 * Event: evidence_signup_wall_viewed
 */
export type EvidenceSignUpWallViewedEventProps = {
  /** The type of wall that triggered the sign-up prompt */
  wall_type: EvidenceSignUpWall;
};

/**
 * Visual surface where an Evidence source click originated.
 * Used to disambiguate which UI element the user interacted with.
 *
 * EVI-1486: unifies the four citation/source surfaces into a single event
 * shape so downstream dashboards can group by `surface` instead of stitching
 * multiple legacy event names together.
 */
export type EvidenceSourceSurface =
  /** Inline markdown anchor inside the assistant's response (badge link). */
  | 'inline_markdown_link'
  /** Inline citation badge popover (the [n] chip with hover-card). */
  | 'inline_citation_badge'
  /** Card in the References list at the end of an Evidence answer. */
  | 'references_section'
  /** Web Search results bar shown above the response. */
  | 'search_block'
  /**
   * Right-side CPD source viewer sheet (the panel rendered by
   * `<SourceViewerSheet>` after a citation is opened in the in-app viewer).
   * Header arrow + iframe-fallback "Open article" both fall under this
   * surface — the user has already entered the viewer and is now choosing
   * to leave it for a real browser tab. EVI-1486 round 3.
   */
  | 'source_viewer_sheet';

/**
 * Coarse content block the source click originated from. Where `surface`
 * describes the *element* (badge, anchor, references card …), `surface_block`
 * describes the *containing region* of the assistant message — the same
 * inline citation badge can appear in the prose body, the Key Points block,
 * or (in future) other answer blocks, and we want to be able to filter
 * "clicks from Key Points" without enumerating every surface in the dashboard.
 *
 * Mostly redundant with `surface` for `references_section`, `search_block`,
 * and `source_viewer_sheet`, but explicit for consistency. EVI-1486 round 4.
 */
export type EvidenceSurfaceBlock =
  /** Main streamed prose body of the assistant response. */
  | 'prose'
  /** "Key Points" callout block at the top of an Evidence answer. */
  | 'key_points'
  /**
   * Prominent clinical safety / drug-interaction callout (EV-023 / QA-173).
   * Separated from `prose` so dashboards can isolate engagement with the
   * highest-risk content surface.
   */
  | 'safety_callout'
  /**
   * Trailing "Key Safety Notes" summary block (EVI-1627), the safety mirror of
   * the top-of-answer Key Points block. Distinct from `safety_callout` (the
   * inline high-risk callout) so dashboards can separate the two surfaces.
   */
  | 'key_safety_notes'
  /** Numbered references list rendered after the body. */
  | 'references_list'
  /** Legacy single Web Search results bar above the response. */
  | 'search_block'
  /**
   * Per-sub-agent inline search block (the collapsible "Search …" /
   * "N sources" rows rendered by `<InlineSearchBlock>` for each web_search
   * tool call). Distinguishes clicks on a single sub-agent's expanded
   * result list from the aggregated rollup below.
   */
  | 'inline_search_block'
  /**
   * Aggregated, deduplicated rollup of every sub-agent's sources for the
   * current message (the "N sources found" card rendered by
   * `<CollapsedSourcesSummary>`). One row per unique URL across the whole
   * answer, so `citation_number` alone uniquely identifies which row was
   * clicked.
   */
  | 'collapsed_sources_summary'
  /**
   * Caption beneath an inline `<EvidenceImageBlock>` figure. The
   * "Source: <hostname>" link emits this so dashboards can separate
   * image-attribution clicks from regular prose markdown links.
   * `citation_number` carries the image's display index (`image.n`).
   * EVI-1486 round 5.
   */
  | 'image_caption'
  /** Right-side CPD source viewer sheet. */
  | 'viewer_sheet';

/**
 * Type of interaction that produced the source click.
 * Optional sub-classification on top of `surface` for funnel analysis.
 */
export type EvidenceSourceInteraction =
  /** Click that opens the source in the in-app source viewer (desktop default). */
  | 'open_in_viewer'
  /** Click/tap that opens the URL in a new browser tab. */
  | 'open_in_new_tab'
  /** Click on the "Go to source" tooltip arrow rather than the badge body. */
  | 'tooltip_go_to_source'
  /** Click that opens an attached library/user document in the preview pane. */
  | 'open_library_document'
  /** Mobile tap on the citation badge (shortcut to the primary source). */
  | 'mobile_tap';

/**
 * Properties for tracking when a user clicks on a source link in the output.
 * Event: evidence_source_clicked
 *
 * EVI-1486: extended with `chat_id`, `message_id`, `session_id`, `surface`,
 * `interaction`, `source_type`, and `reference_index` so a single event can
 * answer "which user, in which chat/message, clicked which source from
 * which UI surface" without joining multiple legacy events.
 */
export type EvidenceSourceClickedEventProps = {
  /** The URL of the source that was clicked */
  source_url: string;
  /** The domain of the source (e.g., "pubmed.ncbi.nlm.nih.gov") */
  source_domain: string;
  /** The title of the source, if available */
  source_title?: string;
  /** Citation source classification (e.g. 'web', 'user_document', 'integration_partner'). */
  source_type?: string;
  /** Visual surface that fired the event. */
  surface?: EvidenceSourceSurface;
  /** Sub-classification of the interaction within the surface. */
  interaction?: EvidenceSourceInteraction;
  /** Backend chat ID, when available. */
  chat_id?: string;
  /** Backend message ID (msg_*), when available. */
  message_id?: string;
  /** Scribe session ID if the click happened inside a session. */
  session_id?: string;
  /** Position/index of the reference in the references list (0-based). */
  reference_index?: number;
  /**
   * 1-based ordinal of the citation within the assistant message. For an
   * `inline_citation_badge` it is the badge's position in the streamed
   * answer (so duplicate URLs at different points in the response are
   * distinguishable). For `references_section` it matches the visible
   * `[n].` label (i.e. `reference_index + 1`). Undefined when the surface
   * has no notion of citation order (e.g. raw `search_block` source rows).
   * EVI-1486 round 4.
   */
  citation_number?: number;
  /**
   * Containing block in the assistant message where the click originated
   * (prose, key_points, references_list, search_block, viewer_sheet).
   * Lets us answer "did the user click from the summary block or the body?"
   * without re-running NLP over the response. EVI-1486 round 4.
   */
  surface_block?: EvidenceSurfaceBlock;
  /**
   * For `surface_block: 'inline_search_block'`, the natural-language query
   * the sub-agent ran to produce the clicked row (e.g. "ixekizumab side
   * effects adverse events safety profile"). Because every sub-agent
   * search in a single assistant turn shares the same backend
   * `message_id`, two clicks on row 1 of two different sub-agent blocks
   * are otherwise indistinguishable by `(message_id, citation_number)`.
   * This field gives dashboards a per-block dimension to roll up by.
   * Undefined for every other surface_block, including
   * `collapsed_sources_summary` (the rollup is a cross-sub-agent dedup so
   * no single query applies). EVI-1486 round 6.
   */
  search_block_query?: string;
  /**
   * Library collection(s) the document belongs to, when
   * `source_type === 'user_document'`. Plural array — a document can belong
   * to multiple collections, and the backend now streams the full set on
   * the Source object (ml-scribe #8835). Kept as a sibling of
   * `source_domain` rather than overloaded into it so web-source dashboards
   * filtering by domain stay precise. Snowplow Iglu schema is `array of
   * string` (see docs/evi-1486-snowplow-schema-gaps.md). EVI-1486 round 7
   * + EVI-1592.
   */
  collections?: string[];
};

/**
 * Destination classification for `<heidi-button>` clicks. Derived from the
 * resolved URL prefix on the FE so dashboards can group by intent without
 * having to enumerate every href permutation.
 *
 * Re-exported from ``@/components/chat/config/heidi-button-allowlist`` —
 * the allowlist owns the canonical destination union (it is what
 * ``classifyHeidiButtonHref`` actually returns), so the analytics shape
 * follows it automatically as new destinations are added.
 */
export type EvidenceChatCtaDestination = HeidiButtonDestination;

/**
 * Properties for tracking when a user clicks a `<heidi-button>` rendered
 * inline in an Evidence chat response.
 * Event: evidence_chat_cta_clicked
 */
export interface EvidenceChatCtaClickedEventProps extends EvidenceBaseEventProps {
  /** Resolved absolute URL we navigated to (after FE resolution + allowlist check). */
  href: string;
  /** Visible button text the LLM emitted between the tag's opening/closing tokens. */
  label: string;
  /** Coarse classification of the destination — kept stable for dashboard grouping. */
  destination: EvidenceChatCtaDestination;
  /** `window.location.host` at click time — useful for splitting prod vs dev vs registrar. */
  origin_host: string;
  /** Chat ID, when available from chat context. */
  chat_id?: string;
  /** Backend message ID (msg_*), when available. */
  message_id?: string;
  /** Session ID if the click happened inside a scribe session. */
  session_id?: string;
}

/**
 * Properties for tracking when a user submits feedback on a reference/source.
 * Event: evidence_reference_feedback_submitted
 */
export interface EvidenceReferenceFeedbackSubmittedEventProps extends EvidenceBaseEventProps {
  /** The feedback type: positive (thumbs up) or negative (thumbs down) */
  feedback_type: 'positive' | 'negative';
  /** The URL of the source being rated */
  source_url: string;
  /** The domain of the source (e.g., "pubmed.ncbi.nlm.nih.gov") */
  source_domain: string;
  /** The title of the source, if available */
  source_title?: string;
  /** The position/index of the reference in the list (0-based) */
  reference_index: number;
  /** The chat ID where feedback was given */
  chat_id?: string;
  /** The session ID if in session */
  session_id?: string;
  /** The message ID of the assistant response containing this reference */
  message_id?: string;
  /** Whether the user provided a comment */
  has_comment: boolean;
  /** The user's comment, if provided (for qualitative analysis) */
  user_comment?: string;
}

/**
 * Evidence verb options for the five-verb homepage experience.
 * These are the verb chips: "Look up", "Work up", "Treat", "Prepare", "Write".
 */
export enum EvidenceVerb {
  LookUp = 'look_up',
  WorkUp = 'work_up',
  Treat = 'treat',
  Prepare = 'prepare',
  Write = 'write',
  Fill = 'fill',
  /**
   * The in-session composer's recut set (Direction C) adds these two. The
   * published `evidence_verb_clicked` schema (1-0-1) types `verb` as a free
   * string rather than an enum, so widening this needs no Iglu bump.
   */
  Medications = 'medications',
  Scores = 'scores',
}

/**
 * Properties for tracking when a user clicks on a verb chip.
 * Event: evidence_verb_clicked
 */
export type EvidenceVerbClickedEventProps = {
  /** The verb that was clicked */
  verb: EvidenceVerb;
  /** Whether the popover was expanded (true) or collapsed (false) */
  is_expanded: boolean;
  /**
   * Which surface the chip was clicked on. The homepage empty state has never
   * sent this, which is why in-session and out-of-session chip usage cannot be
   * told apart today; the in-session composer sends it. Already present in the
   * published schema, so no Iglu bump.
   */
  evidence_location?: EvidenceLocation;
  /**
   * Assigned in-session capability-signalling arm. Amplitude-only — the
   * published Snowplow schema (1-0-1) does not include this field, so the
   * evidence Snowplow handler strips it before Iglu.
   */
  experiment_arm?: CapabilitySignallingVariant;
};

/**
 * Mapping from internal verb keys (used in EvidenceHomepageVerbs)
 * to analytics EvidenceVerb enum values.
 */
export const verbToAnalyticsVerb: Record<string, EvidenceVerb> = {
  /**
   * The in-session recut renamed this card's display label to "Guidelines"
   * (Direction C), but the verb id and this analytics value deliberately lag
   * it: `look_up` is what the L1-to-L2 conversion baseline the arm is measured
   * against was recorded under, and re-spelling either would split the join.
   */
  lookUp: EvidenceVerb.LookUp,
  workUp: EvidenceVerb.WorkUp,
  treat: EvidenceVerb.Treat,
  prepare: EvidenceVerb.Prepare,
  write: EvidenceVerb.Write,
  fill: EvidenceVerb.Fill,
  medications: EvidenceVerb.Medications,
  /**
   * The recut chip reads "Calculate". The id and this value stay `scores`
   * so the L1-to-L2 baseline still joins.
   */
  scores: EvidenceVerb.Scores,
};

/**
 * Mapping from internal category keys (used in ClinicalQuestionBank)
 * to analytics EvidenceCategory enum values.
 */
export const categoryToAnalyticsCategory: Record<string, EvidenceCategory> = {
  quickClinicalAnswers: EvidenceCategory.QuickClinicalAnswers,
  diagnosticWorkups: EvidenceCategory.DiagnosticWorkups,
  medicationSafetyAndDosing: EvidenceCategory.MedicationSafetyAndDosing,
  treatmentDecisions: EvidenceCategory.TreatmentDecisions,
  compareTheEvidence: EvidenceCategory.CompareTheEvidence,
  learningAndExamPrep: EvidenceCategory.LearningAndExamPrep,
  topicOverviews: EvidenceCategory.TopicOverviews,
};

/**
 * Properties for tracking when a non-admin team member requests access to Evidence Pro.
 * Event: evidence_request_access_clicked
 */
export interface EvidenceRequestAccessClickedEventProps extends EvidenceBaseEventProps {
  /** The team ID of the user */
  team_id?: string;
}

/**
 * Properties for tracking when a team admin clicks contact sales.
 * Event: evidence_contact_sales_clicked
 */
export interface EvidenceContactSalesClickedEventProps extends EvidenceBaseEventProps {
  /** The team ID of the admin */
  team_id?: string;
}

/**
 * Properties for tracking when an NHS user submits contact sales for evidence.
 * Event: evidence_nhs_contact_sales_submitted
 */
export interface EvidenceNhsContactSalesSubmittedEventProps extends EvidenceBaseEventProps {
  /** User's email address */
  email?: string;
  /** User's first name */
  first_name?: string;
  /** User's last name */
  last_name?: string;
  /** User's country (e.g., "United Kingdom") */
  country?: string;
  /** Team ID the user belongs to */
  team_id?: string;
  /** Team name */
  team_name?: string;
  /** Number of members in the team */
  team_size?: number;
  /** User's role in the team (ADMIN or MEMBER) */
  team_role?: string;
  /** Whether the user is an admin of the team */
  is_team_admin?: boolean;
}

/**
 * Properties for tracking when a user submits feedback on an evidence response.
 * Event: evidence_response_feedback_submitted
 */
export interface EvidenceResponseFeedbackSubmittedEventProps extends EvidenceBaseEventProps {
  /** The feedback type: positive (thumbs up) or negative (thumbs down) */
  feedback_type: 'positive' | 'negative';
  /** The chat ID where feedback was given */
  chat_id?: string;
  /** The session ID if in session */
  session_id?: string;
  /** The message ID of the assistant response being rated (from backend) */
  message_id?: string;
  /** Whether the user provided a comment */
  has_comment: boolean;
  /** The position/index of the response in the conversation */
  response_position?: number;
  /** The user's feedback comment (for qualitative analysis) */
  user_comment?: string;
  /** The chat mode: "legacy" or "evidence" */
  mode?: 'legacy' | 'evidence';
}

/**
 * Properties for tracking when the evidence onboarding modal is viewed.
 * Event: evidence_onboarding_viewed
 */
export interface EvidenceOnboardingViewedEventProps {
  /** Whether the user has a specialty already set */
  has_existing_specialty: boolean;
  /** Whether the user has a jurisdiction already set */
  has_existing_jurisdiction: boolean;
}

/**
 * Properties for tracking when the user saves preferences in the evidence onboarding modal.
 * Event: evidence_onboarding_saved
 */
export interface EvidenceOnboardingSavedEventProps {
  /** The specialty ID that was saved */
  specialty_id?: string;
  /** The jurisdiction code that was saved (e.g., "AU-NSW") */
  jurisdiction?: string;
  /** Whether the specialty was changed from an existing value */
  specialty_changed: boolean;
  /** Whether the jurisdiction was changed from an existing value */
  jurisdiction_changed: boolean;
}

/**
 * Properties for tracking when the user skips the evidence onboarding modal.
 * Event: evidence_onboarding_skipped
 */
export type EvidenceOnboardingSkippedEventProps = Record<string, never>;

/**
 * Properties for tracking when the user dismisses the evidence onboarding modal.
 * Event: evidence_onboarding_dismissed
 */
export type EvidenceOnboardingDismissedEventProps = Record<string, never>;

/**
 * Properties for tracking when a user clicks a reference to scroll to its inline citation.
 * Event: evidence_reference_scroll_clicked
 */
export interface EvidenceReferenceScrollClickedEventProps extends EvidenceBaseEventProps {
  /** Position of the reference in the list (0-based) */
  reference_index: number;
  /** The URL of the source */
  source_url: string;
  /** The domain of the source */
  source_domain: string;
  /** Whether a matching inline citation was found and scrolled to */
  scroll_succeeded: boolean;
  /** The chat ID */
  chat_id?: string;
  /**
   * Backend message ID (msg_*), when available — added so scroll events
   * join to the same message row as `evidence_source_clicked`.
   */
  message_id?: string;
  /**
   * Citation source classification (e.g. 'web', 'user_document',
   * 'integration_partner') — lets dashboards slice "scrolled to library
   * doc" vs. "scrolled to web source" without joining on `reference_index`.
   */
  source_type?: string;
  /** The title of the reference, when available. */
  source_title?: string;
  /**
   * Library collection(s) the document belongs to (only set when
   * `source_type === 'user_document'`). Plural array; parity with
   * `EvidenceSourceClickedEventProps.collections`. EVI-1486 round 7 +
   * EVI-1592.
   */
  collections?: string[];
}

/**
 * Properties for tracking when a user expands or collapses the references section.
 * Event: evidence_references_section_toggled
 */
export interface EvidenceReferencesSectionToggledEventProps extends EvidenceBaseEventProps {
  /** New state after toggle: true = expanded, false = collapsed */
  is_expanded: boolean;
  /** Number of references in the section */
  references_count: number;
  /** What triggered the toggle: 'manual' = user clicked, 'auto' = auto-expand on stream complete */
  trigger: 'manual' | 'auto';
  /** The chat ID */
  chat_id?: string;
  /**
   * Backend message ID (msg_*) of the assistant response whose references were
   * toggled — joins the expand/collapse to the same message row as
   * `evidence_source_clicked`, and therefore to the producing model.
   */
  message_id?: string;
}

// ============================================================================
// Share Evidence Chat Events
// ============================================================================

/**
 * Properties for tracking when a user opens the share modal.
 * Event: evidence_share_modal_opened
 */
export interface EvidenceShareModalOpenedEventProps extends EvidenceBaseEventProps {
  /** The chat ID being shared */
  chat_id: string;
}

/**
 * Properties for tracking when a user copies the share link.
 * Event: evidence_share_link_copied
 */
export interface EvidenceShareLinkCopiedEventProps extends EvidenceBaseEventProps {
  /** The chat ID being shared */
  chat_id: string;
}

/**
 * Social platforms available for sharing.
 */
export type EvidenceShareSocialPlatform = 'x' | 'linkedin' | 'reddit' | 'whatsapp';

/**
 * Properties for tracking when a user clicks a social share button.
 * Event: evidence_share_social_clicked
 */
export interface EvidenceShareSocialClickedEventProps extends EvidenceBaseEventProps {
  /** The chat ID being shared */
  chat_id: string;
  /** The social platform clicked */
  platform: EvidenceShareSocialPlatform;
}

/**
 * Properties for tracking when a shared chat is opened.
 * Event: evidence_shared_chat_opened
 */
export interface EvidenceSharedChatOpenedEventProps {
  /** The share token from the URL */
  share_token: string;
  /** Whether the user opening is authenticated */
  is_authenticated: boolean;
}

// ============================================================================
// Evidence Copy Confirmation Events (EVI-1452 / SWR-88)
// ============================================================================

/**
 * Lifecycle of the Evidence copy-to-clipboard PHI/PII confirmation popover.
 *
 * - `shown`: popover was rendered to the user.
 * - `confirmed`: user attested no PHI/PII and clipboard write proceeded.
 * - `dismissed`: user backed out; clipboard write did not occur.
 *
 * Forms the audit trail required by med-device gap SWR-88 — every copy action
 * the user takes from an Evidence answer is paired with a logged attestation.
 */
export type EvidenceCopyConfirmationAction = 'shown' | 'confirmed' | 'dismissed';

/**
 * Where the copy was attempted from. `reply_actions` is the main per-response copy
 * button; `user_notes` is the copy button on a `<usernotes>` block (EVI-608).
 */
export type EvidenceCopyConfirmationSurface = 'reply_actions' | 'user_notes';

/**
 * Properties for tracking the Evidence copy-to-clipboard PHI/PII confirmation popover.
 * Event: evidence_copy_confirmation
 */
export interface EvidenceCopyConfirmationEventProps extends EvidenceBaseEventProps {
  /** Lifecycle stage of the confirmation popover. */
  action: EvidenceCopyConfirmationAction;
  /** Which copy button surface triggered the gate. */
  surface: EvidenceCopyConfirmationSurface;
  /** The chat ID being copied from. */
  chat_id: string;
  /**
   * Backend message ID (msg_*) of the assistant response being copied — joins the
   * copy (an adoption signal) to the specific model that produced the response.
   */
  message_id?: string;
}

/**
 * Properties for tracking a successful Evidence chat copy-to-clipboard.
 * Event: evidence_copied (EVI-2101)
 *
 * Sibling to `evidence_copy_confirmation` (PHI gate lifecycle) and companion
 * adoption signal to `note_insert_applied` (add/replace/create-document).
 * Emit only after a successful clipboard write.
 */
export interface EvidenceCopiedEventProps extends EvidenceBaseEventProps {
  /** Which copy button surface succeeded. */
  surface: EvidenceCopyConfirmationSurface;
  /** Chat ID the copied content came from. */
  chat_id?: string;
  /**
   * Backend message ID (msg_*) of the assistant response being copied — joins the
   * copy to the specific model that produced the response.
   */
  message_id?: string;
  /** Session ID when the chat is linked to a session (in-session vs standalone). */
  session_id?: string;
  /** Chat surface mode when known (e.g. 'evidence'). */
  mode?: string;
}

/**
 * Properties for tracking when the source control popover is toggled open/closed.
 * Event: evidence_source_control_toggled
 */
export interface EvidenceSourceControlToggledEventProps extends EvidenceBaseEventProps {
  /** Whether the popover is now open (true) or closed (false) */
  is_open: boolean;
  /** What triggered the toggle: 'manual' = user clicked, 'auto' = auto-triggered */
  trigger: 'manual' | 'auto';
  /** Number of active source filters when toggled */
  active_filters_count?: number;
}

/**
 * Properties for tracking when live suggestions are toggled on/off.
 * Event: evidence_live_suggestions_toggled
 */
export interface EvidenceLiveSuggestionsToggledEventProps extends EvidenceBaseEventProps {
  /** Whether suggestions are now enabled (true) or disabled (false) */
  is_enabled: boolean;
  /** Number of enabled categories when toggled */
  enabled_categories_count: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Experience Feedback Events
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Properties for tracking when the experience feedback nudge is shown.
 * Event: evidence_experience_feedback_viewed
 */
export interface EvidenceExperienceFeedbackViewedEventProps extends EvidenceBaseEventProps {
  /** Number of completed queries when nudge was shown */
  query_count: number;
  /** Number of times the user has previously snoozed */
  snooze_count: number;
}

/**
 * Properties for tracking when a user clicks a star rating.
 * Event: evidence_experience_feedback_rating_submitted
 *
 * This event fires immediately on star click to guarantee rating capture
 * before the user potentially navigates away.
 */
export interface EvidenceExperienceFeedbackRatingSubmittedEventProps extends EvidenceBaseEventProps {
  /** Which scale was rated: ease or usefulness */
  rating_type: 'ease' | 'usefulness';
  /** Rating from 1 (worst) to 5 (best) */
  rating: number;
  /** Number of completed queries when rating was submitted */
  query_count: number;
  /** Number of times the user snoozed before rating */
  snooze_count: number;
  /** The chat ID where feedback was given */
  chat_id?: string;
  /** The session ID if in session */
  session_id?: string;
}

/**
 * Properties for tracking when a user submits experience feedback.
 * Event: evidence_experience_feedback_submitted
 *
 * This event fires when user clicks Submit after optionally entering a comment.
 * May not fire if user closes the nudge after rating without submitting.
 */
export interface EvidenceExperienceFeedbackSubmittedEventProps extends EvidenceBaseEventProps {
  /** Ease rating (UMUX-Lite) 1–5 */
  ease_rating: number;
  /** Usefulness rating (UMUX-Lite) 1–5 */
  usefulness_rating: number;
  /** @deprecated Use ease_rating / usefulness_rating. Kept for backward compatibility. */
  rating?: number;
  /** Whether the user provided a comment */
  has_comment: boolean;
  /** Length of the comment in characters */
  comment_length: number;
  /** The comment text (empty string if none provided) */
  comment: string;
  /** Number of completed queries when feedback was submitted */
  query_count_at_trigger: number;
  /** Number of times the user snoozed before submitting */
  snooze_count_before_submit: number;
  /** Whether the submission failed due to an API error */
  error?: boolean;
  /** The chat ID where feedback was given */
  chat_id?: string;
  /** The session ID if in session */
  session_id?: string;
}

/**
 * Properties for tracking when a user snoozes the experience feedback nudge.
 * Event: evidence_experience_feedback_snoozed
 */
export interface EvidenceExperienceFeedbackSnoozedEventProps extends EvidenceBaseEventProps {
  /** Snooze count after this snooze (1, 2, or 3) */
  snooze_count: number;
  /** Number of completed queries when snoozed */
  query_count: number;
}

/**
 * Properties for tracking when a user dismisses the experience feedback nudge.
 * Event: evidence_experience_feedback_dismissed
 */
export interface EvidenceExperienceFeedbackDismissedEventProps extends EvidenceBaseEventProps {
  /** Number of completed queries when dismissed */
  query_count: number;
}

// ─────────────────────────────────────────────────────────────────────────────
